import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, Calendar, Clock, BookOpen } from "lucide-react";
import SectionHeader from "./SectionHeader";

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image: string | null;
  category: string | null;
  read_time_minutes: number;
  published_at: string | null;
}

const fetchLatest = async (): Promise<Article[]> => {
  const { data } = await supabase
    .from("articles")
    .select("id,title,slug,excerpt,cover_image,category,read_time_minutes,published_at")
    .eq("is_published", true)
    .order("is_pinned", { ascending: false })
    .order("pinned_at", { ascending: false, nullsFirst: false })
    .order("sort_order", { ascending: true })
    .order("published_at", { ascending: false })
    .limit(4);
  return (data as Article[]) || [];
};

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const BabyCareTipsSection = () => {
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["home-articles"],
    queryFn: fetchLatest,
    staleTime: 5 * 60 * 1000,
  });

  // Hide section if no published articles
  if (!isLoading && articles.length === 0) return null;

  return (
    <section data-section-id="baby_care_tips" className="bg-secondary py-8 md:py-14">
      <div className="mx-auto max-w-[1800px] px-3 md:px-6">
        <div className="rounded-2xl md:rounded-3xl border border-border bg-card p-4 md:p-8 shadow-sm">
          <SectionHeader
            title="Baby Care Tips & Articles"
            viewAllHref={articles.length > 0 ? "/baby-care-tips" : undefined}
            viewAllLabel="View All"
          />

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] rounded-2xl bg-muted" />
                <div className="mt-3 h-3 w-1/3 rounded bg-muted" />
                <div className="mt-2 h-4 w-4/5 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {articles.map((a) => (
              <Link
                key={a.id}
                to={`/baby-care-tips/${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl bg-card shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  {a.cover_image ? (
                    <img
                      src={a.cover_image}
                      alt={a.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      <BookOpen size={32} />
                    </div>
                  )}
                  {a.category && (
                    <span className="absolute top-2 left-2 rounded-full bg-primary/95 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground shadow-sm md:text-[10px]">
                      {a.category}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-3 md:p-4">
                  <h3 className="line-clamp-2 font-body text-sm font-semibold leading-snug text-foreground transition-colors group-hover:text-primary md:text-base">
                    {a.title}
                  </h3>
                  {a.excerpt && (
                    <p className="mt-1 line-clamp-2 hidden text-xs text-muted-foreground md:block">
                      {a.excerpt}
                    </p>
                  )}
                  <div className="mt-auto flex items-center gap-2 pt-2 text-[10px] text-muted-foreground md:text-xs">
                    {a.published_at && (
                      <span className="flex items-center gap-1">
                        <Calendar size={10} /> {formatDate(a.published_at)}
                      </span>
                    )}
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <Clock size={10} /> {a.read_time_minutes} min
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

          <div className="mt-6 flex justify-center md:hidden">
            <Link
              to="/baby-care-tips"
              className="inline-flex items-center gap-1 whitespace-nowrap border-b border-primary pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary"
            >
              View All Articles <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BabyCareTipsSection;
